<template>
  <div>
    <div class="d-flex align-center mb-6 px-1">
      <div class="caption font-weight-black text-uppercase tracking-widest grey--text">Listado de Proveedores</div>
      <v-spacer></v-spacer>
      <v-btn
        color="primary"
        outlined
        class="font-weight-black text-uppercase tracking-widest mr-3 rounded-lg px-6 h-44 shadow-xs"
        @click="$router.push({name:'ImportProviders'})"
      >
        <v-icon left size="16">fas fa-file-import</v-icon>
        Importar
      </v-btn>
      <v-btn
        color="success"
        class="font-weight-black text-uppercase tracking-widest rounded-lg px-8 h-44 shadow-sm"
        @click="openCreate()"
      >
        <v-icon left size="16">fas fa-plus</v-icon>
        Nuevo
      </v-btn>
    </div>

    <!-- Search Section -->
    <v-card outlined class="bg-lightgrey border-dashed pa-4 mb-6 d-flex align-center rounded-xl">
      <v-text-field
        v-model="search"
        @keyup="searchTimeOut"
        placeholder="Buscar proveedor por nombre o RUT..."
        prepend-inner-icon="fas fa-search"
        outlined
        dense
        hide-details
        class="modern-search-input bg-white elevation-0"
        :loading="loading"
      ></v-text-field>
    </v-card>

    <!-- Table -->
    <v-card outlined class="modern-table-card elevation-0 overflow-hidden">
      <v-data-table
        :headers="headers"
        :items="providers"
        :items-per-page="10"
        :loading="loading"
        :options.sync="options"
        :server-items-length="total"
        dense
        class="modern-data-table"
        :footer-props="{
          showFirstLastPage: true,
          firstIcon: 'fas fa-angle-double-left',
          lastIcon: 'fas fa-angle-double-right',
          prevIcon: 'fas fa-angle-left',
          nextIcon: 'fas fa-angle-right'
        }"
      >
        <template v-slot:item.rut="{ item }">
          <span class="font-weight-bold grey--text text--darken-2">{{ item.rut }}</span>
        </template>
        
        <template v-slot:item.name="{ item }">
          <span class="body-2 font-weight-black primary--text">{{ item.name }}</span>
        </template>

        <template v-slot:item.payment="{ item }">
          <v-chip size="small" label color="primary lighten-5" class="primary--text font-weight-black" small>
            {{ item.payment }} días
          </v-chip>
        </template>

        <template v-slot:item.estatus="{ item }">
          <v-switch 
            v-model="item.estatus" 
            @change="updateStatus(item)"
            hide-details
            dense
            color="success"
            class="ma-0 pa-0 modern-switch"
          ></v-switch>
        </template>

        <template v-slot:item.actions="{ item }">
          <v-btn icon color="primary" small @click.stop="update(item.id)">
            <v-icon size="16">fas fa-edit</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <!-- Refined Dialog -->
    <v-dialog v-model="showDialog" max-width="500" content-class="modern-dialog" persistent>
      <v-card class="overflow-hidden rounded-xl bg-white">
        <provider-create-update :id="provider" @updated="fetch" @close="showDialog=false"/>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { fetchProvider, updateProvider } from '@/api/stocks'
import notifications from '@/utils/notifications'
import ProviderCreateUpdate from '@/components/stocks/ProviderCreateUpdate'

export default {
  name: 'Providers',
  components: { ProviderCreateUpdate },
  data() {
    return {
      loading: false,
      providers: [],
      provider: -1,
      timer: null,
      options: {},
      search: '',
      total: 0,
      showDialog: false,
      headers: [
        { text: 'RUT', align: 'start', value: 'rut', class: 'caption font-weight-black tracking-widest grey--text' },
        { text: 'Nombre / Razón Social', align: 'start', value: 'name', class: 'caption font-weight-black tracking-widest grey--text' },
        { text: 'Cond. Pago', align: 'center', value: 'payment', class: 'caption font-weight-black tracking-widest grey--text', width: '120px' },
        { text: 'Estatus', align: 'center', value: 'estatus', class: 'caption font-weight-black tracking-widest grey--text', width: '100px', sortable: false },
        { text: 'Acciones', value: 'actions', align: 'center', sortable: false, class: 'caption font-weight-black tracking-widest grey--text', width: '80px' }
      ],
    }
  },
  methods: {
    async fetch() {
      this.loading = true
      const { sortBy, sortDesc, page, itemsPerPage } = this.options
      let ordering = ''
      if (sortBy && sortBy.length > 0) {
        ordering = (sortDesc && sortDesc[0] ? '-' : '') + sortBy[0]
      }
      
      const query = `ordering=${ordering}&&page=${page || 1}&&page_size=${itemsPerPage || 10}&&search=${this.search}`
      try {
        const res = await fetchProvider(query)
        this.total = res.count
        this.providers = res.results
      } catch (err) {
        notifications.showError('Error cargando proveedores')
      } finally {
        this.loading = false
      }
    },
    searchTimeOut() {
      if (this.timer) clearTimeout(this.timer)
      this.timer = setTimeout(() => {
        if (this.options.page !== 1) this.options.page = 1
        else this.fetch()
      }, 700)
    },
    openCreate() {
      this.provider = -1
      this.showDialog = true
    },
    update(id) {
      this.provider = id
      this.showDialog = true
    },
    async updateStatus(item) {
      try {
        await updateProvider(item.id, { estatus: item.estatus })
        const state = item.estatus ? 'activado' : 'desactivado'
        notifications.showSuccess(`${item.name} ha sido ${state} correctamente`)
      } catch (e) {
        notifications.showError('Error al actualizar estatus')
        item.estatus = !item.estatus
      }
    },
  },
  watch: {
    options: {
      handler: 'fetch',
      deep: true
    }
  }
}
</script>

<style scoped>
.tracking-widest { letter-spacing: 1.5px; }
.bg-lightgrey { background-color: #f8fbfc !important; }
.border-dashed { border: 1px dashed #e0e4e8 !important; }
.h-44 { height: 44px !important; }
.shadow-sm { box-shadow: 0 4px 12px rgba(var(--v-success-base), 0.2) !important; }
.shadow-xs { box-shadow: 0 2px 4px rgba(0,0,0,0.05) !important; }

.modern-search-input { border-radius: 24px !important; width: 320px; }
.modern-search-input >>> fieldset { border-radius: 24px !important; border: 1px solid #e0e4e8 !important; }

.modern-table-card { border-radius: 12px !important; border: 1px solid #e0e4e8 !important; }
.modern-data-table >>> .v-data-table-header { background-color: #f8fbfc !important; }
.modern-data-table >>> th { height: 48px !important; text-transform: uppercase; font-size: 0.75rem !important; }
.modern-data-table >>> td { height: 56px !important; }

.modern-switch >>> .v-input--selection-controls__ripple { color: var(--v-success-base) !important; }

.modern-dialog { box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25) !important; }
</style>